import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  TablePagination,
  TextField,
  Button,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Receipt as ReceiptIcon,
  Visibility as ViewIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Clear as ClearIcon,
  Casino as CasinoIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { sevenUpDownAPI } from '../../../services/api';

const SevenUpDownBetHistory = () => {
  const [activeTab, setActiveTab] = useState(0);
  
  // All Bets State
  const [bets, setBets] = useState([]);
  const [loadingBets, setLoadingBets] = useState(true);
  const [betPage, setBetPage] = useState(0);
  const [betRowsPerPage, setBetRowsPerPage] = useState(25);
  const [totalBets, setTotalBets] = useState(0);
  const [betSummary, setBetSummary] = useState(null);
  const [betFilters, setBetFilters] = useState({
    status: '',
    bet_option: '',
    user_id: '',
    from_date: '',
    to_date: '',
  });
  
  // Rounds State
  const [rounds, setRounds] = useState([]);
  const [loadingRounds, setLoadingRounds] = useState(true);
  const [roundPage, setRoundPage] = useState(0);
  const [roundRowsPerPage, setRoundRowsPerPage] = useState(25);
  const [totalRounds, setTotalRounds] = useState(0);
  
  // Round Details Dialog
  const [selectedRound, setSelectedRound] = useState(null);
  const [roundBets, setRoundBets] = useState([]);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [loadingRoundBets, setLoadingRoundBets] = useState(false);

  useEffect(() => {
    if (activeTab === 0) {
      fetchBets();
    } else {
      fetchRounds();
    }
  }, [activeTab, betPage, betRowsPerPage, roundPage, roundRowsPerPage]);

  const fetchBets = async () => {
    try {
      setLoadingBets(true);
      const params = {
        limit: betRowsPerPage,
        offset: betPage * betRowsPerPage,
      };
      
      if (betFilters.status) params.status = betFilters.status;
      if (betFilters.bet_option) params.bet_option = betFilters.bet_option;
      if (betFilters.user_id) params.user_id = betFilters.user_id;
      if (betFilters.from_date) params.from_date = betFilters.from_date;
      if (betFilters.to_date) params.to_date = betFilters.to_date;

      const response = await sevenUpDownAPI.getAllBets(params);
      if (response.data.success) {
        setBets(response.data.bets || []);
        setTotalBets(response.data.total || 0);
        setBetSummary(response.data.summary || null);
      }
    } catch (err) {
      console.error('Error fetching bets:', err);
      toast.error(err.response?.data?.message || 'Failed to fetch bet history');
    } finally {
      setLoadingBets(false);
    }
  };

  const fetchRounds = async () => {
    try {
      setLoadingRounds(true);
      const params = {
        limit: roundRowsPerPage,
        offset: roundPage * roundRowsPerPage,
      };

      const response = await sevenUpDownAPI.getRoundHistory(params);
      if (response.data.success) {
        setRounds(response.data.rounds || []);
        setTotalRounds(response.data.total || 0);
      }
    } catch (err) {
      console.error('Error fetching rounds:', err);
      toast.error(err.response?.data?.message || 'Failed to fetch round history');
    } finally {
      setLoadingRounds(false);
    }
  };

  const handleViewRoundBets = async (round) => {
    try {
      setSelectedRound(round);
      setDetailsDialogOpen(true);
      setLoadingRoundBets(true);

      const response = await sevenUpDownAPI.getAdminRoundBets(round.id);
      if (response.data.success) {
        setRoundBets(response.data.bets || []);
      }
    } catch (err) {
      console.error('Error fetching round bets:', err);
      toast.error(err.response?.data?.message || 'Failed to fetch round bets');
    } finally {
      setLoadingRoundBets(false);
    }
  };

  const handleCloseDialog = () => {
    setDetailsDialogOpen(false);
    setSelectedRound(null);
    setRoundBets([]);
  };

  const handleApplyBetFilters = () => {
    setBetPage(0);
    fetchBets();
  };

  const handleClearBetFilters = () => {
    setBetFilters({
      status: '',
      bet_option: '',
      user_id: '',
      from_date: '',
      to_date: '',
    });
    setBetPage(0);
    setTimeout(fetchBets, 100);
  };

  const formatCurrency = (amount) => {
    return `₹${parseFloat(amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString();
  };

  const getOptionColor = (option) => {
    switch (option) {
      case 'DOWN': return '#f44336';
      case 'SEVEN': return '#9c27b0';
      case 'UP': return '#4caf50';
      default: return '#757575';
    }
  };

  const getOptionEmoji = (option) => {
    switch (option) {
      case 'DOWN': return '⬇️';
      case 'SEVEN': return '7️⃣';
      case 'UP': return '⬆️';
      default: return '❓';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'WON': return 'success';
      case 'LOST': return 'error';
      case 'PENDING': return 'warning';
      default: return 'default';
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <Box sx={{ p: 2 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Typography variant="h5" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
            <ReceiptIcon sx={{ mr: 1, fontSize: 24 }} />
            🎲 7 Up Down - Bet History
          </Typography>
          <Button
            variant="outlined"
            size="small"
            startIcon={<RefreshIcon />}
            onClick={activeTab === 0 ? fetchBets : fetchRounds}
            disabled={activeTab === 0 ? loadingBets : loadingRounds}
          >
            Refresh
          </Button>
        </Box>

        {/* Tabs */}
        <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} sx={{ mb: 2 }}>
          <Tab label="All Bets" />
          <Tab label="Round History" />
        </Tabs>

        {/* All Bets Tab */}
        {activeTab === 0 && (
          <>
            {/* Filters */}
            <Card sx={{ mb: 2 }}>
              <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Grid container spacing={1.5} alignItems="center">
                  <Grid item xs={6} sm={2}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Status</InputLabel>
                      <Select
                        value={betFilters.status}
                        label="Status"
                        onChange={(e) => setBetFilters({ ...betFilters, status: e.target.value })}
                      >
                        <MenuItem value="">All</MenuItem>
                        <MenuItem value="WON">Won</MenuItem>
                        <MenuItem value="LOST">Lost</MenuItem>
                        <MenuItem value="PENDING">Pending</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={6} sm={2}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Bet Option</InputLabel>
                      <Select
                        value={betFilters.bet_option}
                        label="Bet Option"
                        onChange={(e) => setBetFilters({ ...betFilters, bet_option: e.target.value })}
                      >
                        <MenuItem value="">All</MenuItem>
                        <MenuItem value="DOWN">⬇️ DOWN</MenuItem>
                        <MenuItem value="SEVEN">7️⃣ SEVEN</MenuItem>
                        <MenuItem value="UP">⬆️ UP</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={6} sm={2}>
                    <TextField
                      fullWidth
                      size="small"
                      label="User ID"
                      value={betFilters.user_id}
                      onChange={(e) => setBetFilters({ ...betFilters, user_id: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={6} sm={2}>
                    <TextField
                      fullWidth
                      size="small"
                      type="date"
                      label="From"
                      value={betFilters.from_date}
                      onChange={(e) => setBetFilters({ ...betFilters, from_date: e.target.value })}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={6} sm={2}>
                    <TextField
                      fullWidth
                      size="small"
                      type="date"
                      label="To"
                      value={betFilters.to_date}
                      onChange={(e) => setBetFilters({ ...betFilters, to_date: e.target.value })}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={6} sm={2}>
                    <Box display="flex" gap={0.5}>
                      <Button
                        variant="contained"
                        size="small"
                        onClick={handleApplyBetFilters}
                        sx={{ minWidth: 'auto', px: 1.5 }}
                      >
                        <SearchIcon fontSize="small" />
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={handleClearBetFilters}
                        sx={{ minWidth: 'auto', px: 1.5 }}
                      >
                        <ClearIcon fontSize="small" />
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* Summary Stats */}
            {betSummary && (
              <Grid container spacing={1.5} sx={{ mb: 2 }}>
                <Grid item xs={6} sm={3}>
                  <Card sx={{ py: 1 }}>
                    <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
                      <Typography variant="caption" color="text.secondary">Total Bets</Typography>
                      <Typography variant="h6" fontWeight="bold">{betSummary.total_bets.toLocaleString()}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Card sx={{ py: 1 }}>
                    <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
                      <Typography variant="caption" color="text.secondary">Total Amount</Typography>
                      <Typography variant="h6" fontWeight="bold">{formatCurrency(betSummary.total_bet_amount)}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Card sx={{ py: 1 }}>
                    <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
                      <Typography variant="caption" color="text.secondary">Total Payout</Typography>
                      <Typography variant="h6" fontWeight="bold" color="error.main">{formatCurrency(betSummary.total_payout)}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Card sx={{ py: 1 }}>
                    <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
                      <Typography variant="caption" color="text.secondary">Net Profit</Typography>
                      <Typography variant="h6" fontWeight="bold" color={betSummary.total_profit >= 0 ? 'success.main' : 'error.main'}>
                        {formatCurrency(betSummary.total_profit)}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            )}

            {/* Bets Table */}
            <Card>
              <TableContainer sx={{ maxHeight: 500 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      {/* <TableCell sx={{ fontSize: '0.75rem', py: 1 }}>Bet ID</TableCell> */}
                      <TableCell sx={{ fontSize: '0.75rem', py: 1 }}>User</TableCell>
                      <TableCell sx={{ fontSize: '0.75rem', py: 1 }}>Round</TableCell>
                      <TableCell align="center" sx={{ fontSize: '0.75rem', py: 1 }}>Bet</TableCell>
                      <TableCell align="right" sx={{ fontSize: '0.75rem', py: 1 }}>Amount</TableCell>
                      <TableCell align="center" sx={{ fontSize: '0.75rem', py: 1 }}>Status</TableCell>
                      <TableCell align="right" sx={{ fontSize: '0.75rem', py: 1 }}>Payout</TableCell>
                      <TableCell align="center" sx={{ fontSize: '0.75rem', py: 1 }}>Result</TableCell>
                      <TableCell sx={{ fontSize: '0.75rem', py: 1 }}>Time</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {loadingBets ? (
                      <TableRow>
                        <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                          <CircularProgress size={32} />
                        </TableCell>
                      </TableRow>
                    ) : bets.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={9} align="center" sx={{ py: 3 }}>
                          <Typography color="text.secondary" variant="body2">No bets found</Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      bets.map((bet) => (
                        <TableRow key={bet.id} hover sx={{ '& td': { py: 0.75 } }}>
                          {/* <TableCell>
                            <Typography variant="caption" fontFamily="monospace">
                              {bet.bet_uuid?.slice(0, 8) || bet.id}
                            </Typography>
                          </TableCell> */}
                          <TableCell>
                            <Box>
                              <Typography variant="caption" fontWeight="bold" display="block">
                                {bet.user?.username || 'Unknown'}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {bet.user?.phone_number || '-'}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption">
                              #{bet.round?.round_number || '-'}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Chip
                              label={`${getOptionEmoji(bet.bet_option)} ${bet.bet_option}`}
                              size="small"
                              sx={{
                                backgroundColor: getOptionColor(bet.bet_option),
                                color: 'white',
                                fontWeight: 'bold',
                                fontSize: '0.7rem',
                                height: 22
                              }}
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" fontWeight="bold">
                              {formatCurrency(bet.amount)}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Chip
                              label={bet.status}
                              size="small"
                              color={getStatusColor(bet.status)}
                              sx={{ fontSize: '0.7rem', height: 20 }}
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="caption" color={bet.status === 'WON' ? 'success.main' : 'text.secondary'}>
                              {formatCurrency(bet.payout_amount)}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            {bet.round?.winning_option && (
                              <Chip
                                label={`${getOptionEmoji(bet.round.winning_option)}`}
                                size="small"
                                sx={{
                                  backgroundColor: getOptionColor(bet.round.winning_option),
                                  color: 'white',
                                  fontSize: '0.7rem',
                                  height: 20,
                                  minWidth: 30
                                }}
                              />
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption" color="text.secondary">
                              {new Date(bet.placed_at).toLocaleTimeString()}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                rowsPerPageOptions={[10, 25, 50, 100]}
                component="div"
                count={totalBets}
                rowsPerPage={betRowsPerPage}
                page={betPage}
                onPageChange={(e, p) => setBetPage(p)}
                onRowsPerPageChange={(e) => { setBetRowsPerPage(parseInt(e.target.value, 10)); setBetPage(0); }}
              />
            </Card>
          </>
        )}

        {/* Round History Tab */}
        {activeTab === 1 && (
          <Card>
            <TableContainer sx={{ maxHeight: 550 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontSize: '0.75rem', py: 1 }}>Round</TableCell>
                    <TableCell align="center" sx={{ fontSize: '0.75rem', py: 1 }}>Dice</TableCell>
                    <TableCell align="center" sx={{ fontSize: '0.75rem', py: 1 }}>Total</TableCell>
                    <TableCell align="center" sx={{ fontSize: '0.75rem', py: 1 }}>Result</TableCell>
                    <TableCell align="right" sx={{ fontSize: '0.75rem', py: 1 }}>Bets</TableCell>
                    <TableCell align="right" sx={{ fontSize: '0.75rem', py: 1 }}>Amount</TableCell>
                    <TableCell align="right" sx={{ fontSize: '0.75rem', py: 1 }}>Payout</TableCell>
                    <TableCell align="right" sx={{ fontSize: '0.75rem', py: 1 }}>Profit</TableCell>
                    <TableCell sx={{ fontSize: '0.75rem', py: 1 }}>Time</TableCell>
                    <TableCell align="center" sx={{ fontSize: '0.75rem', py: 1 }}>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loadingRounds ? (
                    <TableRow>
                      <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                        <CircularProgress size={32} />
                      </TableCell>
                    </TableRow>
                  ) : rounds.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={10} align="center" sx={{ py: 3 }}>
                        <Typography color="text.secondary" variant="body2">No rounds found</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    rounds.map((round) => (
                      <TableRow key={round.id} hover sx={{ '& td': { py: 0.75 } }}>
                        <TableCell>
                          <Typography variant="caption" fontWeight="bold">#{round.round_number}</Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="caption">🎲{round.dice1_value} + 🎲{round.dice2_value}</Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2" fontWeight="bold">{round.total_dice_sum}</Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={`${getOptionEmoji(round.winning_option)} ${round.winning_option}`}
                            size="small"
                            sx={{
                              backgroundColor: getOptionColor(round.winning_option),
                              color: 'white',
                              fontWeight: 'bold',
                              fontSize: '0.7rem',
                              height: 22
                            }}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="caption">{round.total_bets_count || 0}</Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="caption" fontWeight="bold">{formatCurrency(round.total_bet_amount)}</Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="caption" color="error.main">{formatCurrency(round.total_payout_amount)}</Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="caption" fontWeight="bold" color={parseFloat(round.house_profit) >= 0 ? 'success.main' : 'error.main'}>
                            {formatCurrency(round.house_profit)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" color="text.secondary">
                            {round.result_declared_at ? new Date(round.result_declared_at).toLocaleTimeString() : '-'}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title="View Bets">
                            <IconButton size="small" color="primary" onClick={() => handleViewRoundBets(round)}>
                              <ViewIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[10, 25, 50, 100]}
              component="div"
              count={totalRounds}
              rowsPerPage={roundRowsPerPage}
              page={roundPage}
              onPageChange={(e, p) => setRoundPage(p)}
              onRowsPerPageChange={(e) => { setRoundRowsPerPage(parseInt(e.target.value, 10)); setRoundPage(0); }}
            />
          </Card>
        )}

        {/* Round Details Dialog */}
        <Dialog open={detailsDialogOpen} onClose={handleCloseDialog} maxWidth="lg" fullWidth>
          <DialogTitle sx={{ py: 1.5 }}>
            <Box display="flex" alignItems="center" gap={1}>
              <CasinoIcon color="primary" fontSize="small" />
              <Typography variant="subtitle1" fontWeight="bold">
                Round #{selectedRound?.round_number} - Bet Details
              </Typography>
            </Box>
          </DialogTitle>
          <DialogContent>
            {selectedRound && (
              <>
                <Grid container spacing={1.5} sx={{ mb: 2 }}>
                  <Grid item xs={6} sm={3}>
                    <Paper sx={{ p: 1.5, textAlign: 'center' }}>
                      <Typography variant="caption" color="text.secondary">Dice Roll</Typography>
                      <Typography variant="body1" fontWeight="bold">
                        🎲{selectedRound.dice1_value} + 🎲{selectedRound.dice2_value} = {selectedRound.total_dice_sum}
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Paper sx={{ p: 1.5, textAlign: 'center' }}>
                      <Typography variant="caption" color="text.secondary">Result</Typography>
                      <Box>
                        <Chip
                          label={`${getOptionEmoji(selectedRound.winning_option)} ${selectedRound.winning_option}`}
                          size="small"
                          sx={{ backgroundColor: getOptionColor(selectedRound.winning_option), color: 'white', fontWeight: 'bold' }}
                        />
                      </Box>
                    </Paper>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Paper sx={{ p: 1.5, textAlign: 'center' }}>
                      <Typography variant="caption" color="text.secondary">Total Bets</Typography>
                      <Typography variant="body1" fontWeight="bold">{formatCurrency(selectedRound.total_bet_amount)}</Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Paper sx={{ p: 1.5, textAlign: 'center' }}>
                      <Typography variant="caption" color="text.secondary">House Profit</Typography>
                      <Typography variant="body1" fontWeight="bold" color={parseFloat(selectedRound.house_profit) >= 0 ? 'success.main' : 'error.main'}>
                        {formatCurrency(selectedRound.house_profit)}
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>

                <Divider sx={{ my: 1.5 }} />

                <Typography variant="subtitle2" gutterBottom>Bets ({roundBets.length})</Typography>

                {loadingRoundBets ? (
                  <Box display="flex" justifyContent="center" py={3}>
                    <CircularProgress size={28} />
                  </Box>
                ) : roundBets.length === 0 ? (
                  <Typography color="text.secondary" variant="body2" textAlign="center" py={2}>No bets in this round</Typography>
                ) : (
                  <TableContainer component={Paper} sx={{ maxHeight: 300 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontSize: '0.7rem' }}>User</TableCell>
                          <TableCell align="center" sx={{ fontSize: '0.7rem' }}>Bet</TableCell>
                          <TableCell align="right" sx={{ fontSize: '0.7rem' }}>Amount</TableCell>
                          <TableCell align="center" sx={{ fontSize: '0.7rem' }}>Status</TableCell>
                          <TableCell align="right" sx={{ fontSize: '0.7rem' }}>Payout</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {roundBets.map((bet) => (
                          <TableRow key={bet.id} hover sx={{ '& td': { py: 0.5 } }}>
                            <TableCell>
                              <Typography variant="caption">{bet.user?.name || bet.user?.username || 'Unknown'}</Typography>
                            </TableCell>
                            <TableCell align="center">
                              <Chip
                                label={`${getOptionEmoji(bet.bet_option)}`}
                                size="small"
                                sx={{ backgroundColor: getOptionColor(bet.bet_option), color: 'white', height: 18, minWidth: 28 }}
                              />
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="caption" fontWeight="bold">{formatCurrency(bet.amount)}</Typography>
                            </TableCell>
                            <TableCell align="center">
                              <Chip label={bet.status} size="small" color={getStatusColor(bet.status)} sx={{ height: 18, fontSize: '0.65rem' }} />
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="caption" color={bet.status === 'WON' ? 'success.main' : 'text.secondary'}>
                                {formatCurrency(bet.payout_amount)}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </>
            )}
          </DialogContent>
          <DialogActions sx={{ py: 1 }}>
            <Button size="small" onClick={handleCloseDialog}>Close</Button>
          </DialogActions>
        </Dialog>
      </Box>
    </motion.div>
  );
};

export default SevenUpDownBetHistory;
